import type { CSSProperties } from "react";

/**
 * Guest identity for anonymous visitors.
 *
 * A signed-out learner still appears in session presence (the active-users
 * roster, the "X joined" toast, the completion peers). Without a name they all
 * showed up as "Anonymous User" / "Someone", which reads like a bug and makes
 * the roster impossible to tell apart.
 *
 * So an anonymous id (`anonymous-…`) is turned into a stable, friendly name:
 * one colour + one animal + "(Guest)" — e.g. "Amber Fox (Guest)". The name is
 * *derived* from the id, not stored alongside it: the id already lives in
 * `sessionStorage` (`anonymous-user-id`), so every client computes the same
 * name for the same visitor, and a reconnect does not re-roll it.
 */

const ANONYMOUS_PREFIX = "anonymous-";
const GUEST_SUFFIX = "(Guest)";

export interface GuestColor {
  name: string;
  hex: string;
}

/**
 * Warm/neutral ramps stay out of this list: these are identity colours, so
 * they have to be distinguishable from the stone paper surfaces and from each
 * other at 10px initials.
 */
export const GUEST_COLORS: readonly GuestColor[] = [
  { name: "Amber", hex: "#d97706" },
  { name: "Blue", hex: "#2563eb" },
  { name: "Coral", hex: "#f43f5e" },
  { name: "Cyan", hex: "#0891b2" },
  { name: "Emerald", hex: "#059669" },
  { name: "Indigo", hex: "#4f46e5" },
  { name: "Lime", hex: "#65a30d" },
  { name: "Pink", hex: "#db2777" },
  { name: "Plum", hex: "#9333ea" },
  { name: "Tangerine", hex: "#ea580c" },
  { name: "Teal", hex: "#0d9488" },
  { name: "Violet", hex: "#7c3aed" },
];

export const GUEST_ANIMALS: readonly string[] = [
  "Badger",
  "Bear",
  "Bison",
  "Cat",
  "Crane",
  "Deer",
  "Dolphin",
  "Eagle",
  "Ferret",
  "Finch",
  "Fox",
  "Gecko",
  "Heron",
  "Koala",
  "Lemur",
  "Lynx",
  "Moth",
  "Otter",
  "Owl",
  "Panda",
  "Puma",
  "Quail",
  "Raven",
  "Seal",
  "Sparrow",
  "Swan",
  "Tiger",
  "Toad",
  "Wolf",
  "Wren",
  "Yak",
  "Zebra",
];

export interface GuestIdentity {
  color: GuestColor;
  animal: string;
  /** e.g. "Amber Fox (Guest)" — the string shown in presence UI. */
  displayName: string;
  /** Tinted fallback for `UserAvatar` so the guest's colour follows the name. */
  avatarStyle: CSSProperties;
}

/** True for a missing id or one of our `anonymous-…` guest ids. */
export function isAnonymousUserId(userId?: string | null): boolean {
  return !userId || userId.startsWith(ANONYMOUS_PREFIX);
}

/** Create the guest id that lives in `sessionStorage` for this tab. */
export function createAnonymousUserId(): string {
  return `${ANONYMOUS_PREFIX}${Math.random().toString(36).substring(2, 10)}`;
}

/** FNV-1a — small, synchronous, and stable across clients for the same id. */
function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function pick<T>(list: readonly T[], seed: string): T {
  return list[hashString(seed) % list.length];
}

/**
 * Resolve the display identity for an anonymous id. Returns `null` for a
 * signed-in user (their own name wins).
 */
export function getGuestIdentity(userId?: string | null): GuestIdentity | null {
  if (!isAnonymousUserId(userId)) return null;

  const seed = userId || ANONYMOUS_PREFIX;
  const color = pick(GUEST_COLORS, `${seed}:color`);
  const animal = pick(GUEST_ANIMALS, `${seed}:animal`);

  return {
    color,
    animal,
    displayName: `${color.name} ${animal} ${GUEST_SUFFIX}`,
    avatarStyle: {
      backgroundImage: "none",
      backgroundColor: `${color.hex}1f`,
      color: color.hex,
    },
  };
}

/** Convenience for presence payloads: the name to broadcast, or `null`. */
export function getGuestDisplayName(userId?: string | null): string | null {
  return getGuestIdentity(userId)?.displayName ?? null;
}
